import { jsPDF } from 'jspdf';
import { supabase } from '../lib/supabase';

// Convert snake_case to camelCase
function toCamelCase(obj) {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(toCamelCase);
  
  return Object.keys(obj)?.reduce((acc, key) => {
    const camelKey = key?.replace(/_([a-z])/g, (_, letter) => letter?.toUpperCase());
    acc[camelKey] = toCamelCase(obj?.[key]);
    return acc;
  }, {});
}

export const receiptService = {
  /**
   * Generate transaction receipt PDF
   * @param {Object} payoutData - Payout transaction data
   * @returns {void} Downloads PDF file
   */
  generateTransactionReceipt(payoutData) {
    try {
      const doc = new jsPDF();
      const pageWidth = doc?.internal?.pageSize?.getWidth();
      const pageHeight = doc?.internal?.pageSize?.getHeight();
      
      // Header
      doc?.setFillColor(102, 126, 234);
      doc?.rect(0, 0, pageWidth, 40, 'F');
      doc?.setTextColor(255, 255, 255);
      doc?.setFontSize(24);
      doc?.setFont(undefined, 'bold');
      doc?.text('Transaction Receipt', pageWidth / 2, 20, { align: 'center' });
      doc?.setFontSize(12);
      doc?.setFont(undefined, 'normal');
      doc?.text('Rental Earnings Payout', pageWidth / 2, 30, { align: 'center' });
      
      // Reset text color
      doc?.setTextColor(0, 0, 0);
      
      // Receipt Details
      let yPos = 55;
      doc?.setFontSize(10);
      doc?.setTextColor(100, 100, 100);
      doc?.text(`Receipt Date: ${new Date()?.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}`, 20, yPos);
      doc?.text(`Transaction ID: ${payoutData?.stripeTransferId || payoutData?.id}`, pageWidth - 20, yPos, { align: 'right' });
      
      // Owner Information
      yPos += 15;
      doc?.setFontSize(14);
      doc?.setTextColor(0, 0, 0);
      doc?.setFont(undefined, 'bold');
      doc?.text('Owner Information', 20, yPos);
      doc?.setFont(undefined, 'normal');
      doc?.setFontSize(10);
      yPos += 8;
      doc?.text(`Name: ${payoutData?.ownerName || 'N/A'}`, 20, yPos);
      yPos += 6;
      doc?.text(`Email: ${payoutData?.ownerEmail || 'N/A'}`, 20, yPos);
      
      // Rental Details
      yPos += 15;
      doc?.setFontSize(14);
      doc?.setFont(undefined, 'bold');
      doc?.text('Rental Details', 20, yPos);
      doc?.setFont(undefined, 'normal');
      doc?.setFontSize(10);
      yPos += 8;
      doc?.text(`Trailer: ${payoutData?.trailerTitle || 'N/A'}`, 20, yPos);
      yPos += 6;
      doc?.text(`Rental Period: ${new Date(payoutData?.startDate)?.toLocaleDateString()} - ${new Date(payoutData?.endDate)?.toLocaleDateString()}`, 20, yPos);
      yPos += 6;
      doc?.text(`Booking Reference: ${payoutData?.bookingReference || 'N/A'}`, 20, yPos);
      
      // Payment Breakdown
      yPos += 15;
      doc?.setFontSize(14);
      doc?.setFont(undefined, 'bold');
      doc?.text('Payment Breakdown', 20, yPos);
      
      // Table
      yPos += 10;
      doc?.setFillColor(243, 244, 246);
      doc?.rect(20, yPos - 5, pageWidth - 40, 8, 'F');
      doc?.setFontSize(10);
      doc?.setFont(undefined, 'bold');
      doc?.text('Description', 25, yPos);
      doc?.text('Amount', pageWidth - 25, yPos, { align: 'right' });
      
      // Gross Amount
      yPos += 10;
      doc?.setFont(undefined, 'normal');
      doc?.text('Rental Earnings (Gross)', 25, yPos);
      doc?.text(`$${parseFloat(payoutData?.grossAmount || 0)?.toFixed(2)}`, pageWidth - 25, yPos, { align: 'right' });
      
      // Platform Fee
      yPos += 8;
      doc?.setTextColor(220, 38, 38);
      doc?.text('Platform Fee (15%)', 25, yPos);
      doc?.text(`-$${parseFloat(payoutData?.platformFeeAmount || 0)?.toFixed(2)}`, pageWidth - 25, yPos, { align: 'right' });
      
      // Divider
      yPos += 5;
      doc?.setDrawColor(0, 0, 0);
      doc?.setLineWidth(0.5);
      doc?.line(20, yPos, pageWidth - 20, yPos);
      
      // Net Payout
      yPos += 8;
      doc?.setTextColor(16, 185, 129);
      doc?.setFontSize(12);
      doc?.setFont(undefined, 'bold');
      doc?.text('Net Payout', 25, yPos);
      doc?.text(`$${parseFloat(payoutData?.netAmount || 0)?.toFixed(2)}`, pageWidth - 25, yPos, { align: 'right' });
      
      // Deposit Information
      yPos += 15;
      doc?.setTextColor(0, 0, 0);
      doc?.setFontSize(14);
      doc?.text('Deposit Information', 20, yPos);
      doc?.setFont(undefined, 'normal');
      doc?.setFontSize(10);
      yPos += 8;
      doc?.text(`Deposit Date: ${new Date(payoutData?.payoutDate)?.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}`, 20, yPos);
      yPos += 6;
      doc?.text(`Bank Account: ****${payoutData?.accountLast4 || '****'}`, 20, yPos);
      yPos += 6;
      doc?.text(`Status: Completed`, 20, yPos);
      
      // Footer
      doc?.setFontSize(8);
      doc?.setTextColor(100, 100, 100);
      doc?.text('This is an official transaction receipt for your records.', pageWidth / 2, pageHeight - 20, { align: 'center' });
      doc?.text('For questions, please contact support@platform.com', pageWidth / 2, pageHeight - 15, { align: 'center' });
      
      // Save PDF
      const fileName = `receipt_${payoutData?.id?.substring(0, 8)}_${new Date()?.toISOString()?.split('T')?.[0]}.pdf`;
      doc?.save(fileName);
      
      return { success: true, fileName };
    } catch (error) {
      console.error('Error generating receipt:', error);
      throw error;
    }
  },

  /**
   * Generate 1099-NEC tax form PDF
   * @param {Object} taxData - Annual tax data
   * @returns {void} Downloads PDF file
   */
  async generate1099NEC(year) {
    try {
      const { data: { user }, error: authError } = await supabase?.auth?.getUser();
      
      if (authError || !user) {
        throw new Error('User not authenticated');
      }

      // Fetch annual earnings
      const { data: payouts, error: payoutsError } = await supabase
        ?.from('payout_transactions')
        ?.select('*, bookings(start_date, end_date, booking_reference), trailer_listings(title)')
        ?.eq('user_id', user?.id)
        ?.eq('payout_status', 'completed')
        ?.gte('payout_date', `${year}-01-01`)
        ?.lte('payout_date', `${year}-12-31`);

      if (payoutsError) {
        throw new Error('Failed to fetch annual earnings');
      }

      const camelPayouts = payouts?.map(toCamelCase) || [];
      const totalEarnings = camelPayouts?.reduce((sum, p) => sum + parseFloat(p?.netAmount || 0), 0);
      const totalGross = camelPayouts?.reduce((sum, p) => sum + parseFloat(p?.grossAmount || 0), 0);
      const totalFees = camelPayouts?.reduce((sum, p) => sum + parseFloat(p?.platformFeeAmount || 0), 0);

      // Fetch user profile
      const { data: profile, error: profileError } = await supabase
        ?.from('user_profiles')
        ?.select('full_name, email, phone_number, address')
        ?.eq('user_id', user?.id)
        ?.single();

      if (profileError) {
        throw new Error('Failed to fetch user profile');
      }

      const camelProfile = toCamelCase(profile);

      // Generate PDF
      const doc = new jsPDF();
      const pageWidth = doc?.internal?.pageSize?.getWidth();
      const pageHeight = doc?.internal?.pageSize?.getHeight();
      
      // Header
      doc?.setFillColor(220, 38, 38);
      doc?.rect(0, 0, pageWidth, 35, 'F');
      doc?.setTextColor(255, 255, 255);
      doc?.setFontSize(22);
      doc?.setFont(undefined, 'bold');
      doc?.text('Form 1099-NEC', pageWidth / 2, 15, { align: 'center' });
      doc?.setFontSize(11);
      doc?.setFont(undefined, 'normal');
      doc?.text(`Nonemployee Compensation - Tax Year ${year}`, pageWidth / 2, 25, { align: 'center' });
      
      // Reset text color
      doc?.setTextColor(0, 0, 0);
      
      // Form Details
      let yPos = 50;
      doc?.setFontSize(9);
      doc?.setTextColor(100, 100, 100);
      doc?.text(`Generated: ${new Date()?.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}`, 20, yPos);
      doc?.text('Copy B - For Recipient', pageWidth - 20, yPos, { align: 'right' });
      
      // Payer Information (Platform)
      yPos += 15;
      doc?.setFontSize(12);
      doc?.setTextColor(0, 0, 0);
      doc?.setFont(undefined, 'bold');
      doc?.text('PAYER\'S Information', 20, yPos);
      doc?.setDrawColor(200, 200, 200);
      doc?.setLineWidth(0.3);
      doc?.rect(20, yPos + 2, pageWidth - 40, 35);
      
      doc?.setFont(undefined, 'normal');
      doc?.setFontSize(10);
      yPos += 10;
      doc?.text('Platform Name: Trailer Rental Platform', 25, yPos);
      yPos += 6;
      doc?.text('Address: 123 Business St, City, ST 12345', 25, yPos);
      yPos += 6;
      doc?.text('EIN: XX-XXXXXXX (To be configured)', 25, yPos);
      yPos += 6;
      doc?.text('Phone: (555) 123-4567', 25, yPos);
      
      // Recipient Information (Owner)
      yPos += 15;
      doc?.setFont(undefined, 'bold');
      doc?.setFontSize(12);
      doc?.text('RECIPIENT\'S Information', 20, yPos);
      doc?.rect(20, yPos + 2, pageWidth - 40, 35);
      
      doc?.setFont(undefined, 'normal');
      doc?.setFontSize(10);
      yPos += 10;
      doc?.text(`Name: ${camelProfile?.fullName || 'N/A'}`, 25, yPos);
      yPos += 6;
      doc?.text(`Email: ${camelProfile?.email || 'N/A'}`, 25, yPos);
      yPos += 6;
      doc?.text(`Phone: ${camelProfile?.phoneNumber || 'N/A'}`, 25, yPos);
      yPos += 6;
      doc?.text(`Address: ${camelProfile?.address || 'N/A'}`, 25, yPos);
      
      // Nonemployee Compensation
      yPos += 20;
      doc?.setFillColor(254, 243, 199);
      doc?.rect(20, yPos - 5, pageWidth - 40, 25, 'F');
      doc?.setFontSize(14);
      doc?.setFont(undefined, 'bold');
      doc?.text('Box 1: Nonemployee Compensation', 25, yPos + 5);
      doc?.setFontSize(20);
      doc?.setTextColor(16, 185, 129);
      doc?.text(`$${totalEarnings?.toFixed(2)}`, pageWidth - 25, yPos + 5, { align: 'right' });
      
      // Earnings Breakdown
      yPos += 35;
      doc?.setTextColor(0, 0, 0);
      doc?.setFontSize(12);
      doc?.setFont(undefined, 'bold');
      doc?.text('Annual Earnings Summary', 20, yPos);
      
      yPos += 10;
      doc?.setFillColor(243, 244, 246);
      doc?.rect(20, yPos - 5, pageWidth - 40, 8, 'F');
      doc?.setFontSize(10);
      doc?.text('Description', 25, yPos);
      doc?.text('Amount', pageWidth - 25, yPos, { align: 'right' });
      
      yPos += 10;
      doc?.setFont(undefined, 'normal');
      doc?.text('Total Rental Earnings (Gross)', 25, yPos);
      doc?.text(`$${totalGross?.toFixed(2)}`, pageWidth - 25, yPos, { align: 'right' });
      
      yPos += 8;
      doc?.setTextColor(220, 38, 38);
      doc?.text('Total Platform Fees (15%)', 25, yPos);
      doc?.text(`-$${totalFees?.toFixed(2)}`, pageWidth - 25, yPos, { align: 'right' });
      
      yPos += 5;
      doc?.setDrawColor(0, 0, 0);
      doc?.setLineWidth(0.5);
      doc?.line(20, yPos, pageWidth - 20, yPos);
      
      yPos += 8;
      doc?.setTextColor(16, 185, 129);
      doc?.setFont(undefined, 'bold');
      doc?.text('Net Nonemployee Compensation', 25, yPos);
      doc?.text(`$${totalEarnings?.toFixed(2)}`, pageWidth - 25, yPos, { align: 'right' });
      
      // Transaction Count
      yPos += 15;
      doc?.setTextColor(0, 0, 0);
      doc?.setFont(undefined, 'normal');
      doc?.setFontSize(10);
      doc?.text(`Total Completed Payouts: ${camelPayouts?.length}`, 25, yPos);
      doc?.text(`Tax Year: ${year}`, pageWidth - 25, yPos, { align: 'right' });
      
      // Important Notes
      yPos += 15;
      doc?.setFillColor(239, 246, 255);
      doc?.rect(20, yPos - 5, pageWidth - 40, 30, 'F');
      doc?.setFontSize(9);
      doc?.setFont(undefined, 'bold');
      doc?.text('Important Tax Information:', 25, yPos);
      doc?.setFont(undefined, 'normal');
      yPos += 6;
      doc?.text('• This form reports nonemployee compensation paid to you during the tax year.', 25, yPos);
      yPos += 5;
      doc?.text('• You must report this income on your tax return (Schedule C or Schedule C-EZ).', 25, yPos);
      yPos += 5;
      doc?.text('• Keep this form with your tax records. Consult a tax professional for guidance.', 25, yPos);
      yPos += 5;
      doc?.text('• Platform fees have already been deducted and are not tax-deductible expenses.', 25, yPos);
      
      // Footer
      doc?.setFontSize(7);
      doc?.setTextColor(100, 100, 100);
      doc?.text('This is a computer-generated form. For official IRS Form 1099-NEC, visit www.irs.gov', pageWidth / 2, pageHeight - 15, { align: 'center' });
      doc?.text('Consult with a qualified tax professional for tax filing assistance.', pageWidth / 2, pageHeight - 10, { align: 'center' });
      
      // Save PDF
      const fileName = `1099-NEC_${year}_${user?.id?.substring(0, 8)}.pdf`;
      doc?.save(fileName);
      
      return { success: true, fileName, totalEarnings, payoutCount: camelPayouts?.length };
    } catch (error) {
      console.error('Error generating 1099-NEC:', error);
      throw error;
    }
  }
};

export default receiptService;